import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useAccountOfferId } from '../useAccountOfferId';

import CaseDetailsTab from './CaseDetailsTab';
import CaseFeedTab from './CaseFeedTab';
import CaseRelatedTab from './CaseRelatedTab';
import { CASES_SIDEBAR_LABEL, casesListPath } from './constants';
import { findMockCase } from './mockCases';

const CASE_TABS = [
  { value: 'feed', label: 'Feed' },
  { value: 'details', label: 'Details' },
  { value: 'related', label: 'Related' },
] as const;

type CaseTab = (typeof CASE_TABS)[number]['value'];

export default function CaseDetailPage() {
  const { id_account: accountId = '', caseNumber } = useParams<{ id_account: string; caseNumber: string }>();
  const offerId = useAccountOfferId();
  const [tab, setTab] = useState<CaseTab>('feed');
  const caseItem = findMockCase(caseNumber);

  return (
    <>
      <Helmet>
        <title>{`${CASES_SIDEBAR_LABEL} · ${caseNumber}`}</title>
      </Helmet>

      <Button
        component={Link}
        to={casesListPath(accountId, offerId)}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ textTransform: 'none' }}
      >
        {CASES_SIDEBAR_LABEL}
      </Button>

      {!caseItem ? (
        <Box mt={2}>
          <Alert severity="error">Case {caseNumber} was not found.</Alert>
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: 'secondary.main',
                color: 'common.white',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <WorkIcon />
            </Box>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold">
                {caseItem.number} · {caseItem.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {caseItem.status}
                {caseItem.owner && ` · Owner: ${caseItem.owner}`}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Case sections">
              {CASE_TABS.map((entry) => (
                <Tab key={entry.value} value={entry.value} label={entry.label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ bgcolor: 'action.hover', p: 2, minHeight: 400 }}>
            {tab === 'feed' && <CaseFeedTab caseItem={caseItem} />}
            {tab === 'details' && <CaseDetailsTab caseItem={caseItem} />}
            {tab === 'related' && <CaseRelatedTab caseItem={caseItem} />}
          </Box>
        </>
      )}
    </>
  );
}
